import React, { useEffect, useLayoutEffect, useState } from 'react'

import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { createPortal } from 'react-dom'

import { Button } from '@/shared/components/atoms/Button'
import { cn } from '@/shared/utils/helpers'

export type GuidedTourStep = {
	id: string
	selector: string
	/** Optional selector used when viewport is below md (768px). Use for elements that only exist on mobile (e.g. fixed bottom bar). */
	selectorMobile?: string
	title: string
	description: string
}

interface GuidedTourProps {
	steps: GuidedTourStep[]
	storageKey: string
	open: boolean
	onOpenChange: (open: boolean) => void
}

function getRect(el: Element | null) {
	if (!el) return null
	const r = el.getBoundingClientRect()
	return {
		x: r.x,
		y: r.y,
		width: r.width,
		height: r.height,
		top: r.top,
		left: r.left,
		right: r.right,
		bottom: r.bottom,
	}
}

export const GuidedTour: React.FC<GuidedTourProps> = ({
	steps,
	storageKey,
	open,
	onOpenChange,
}) => {
	const [stepIndex, setStepIndex] = useState(0)
	const step = steps[stepIndex]

	const [isMobile, setIsMobile] = useState(() =>
		typeof window !== 'undefined'
			? window.matchMedia('(max-width: 767px)').matches
			: false
	)
	useEffect(() => {
		const mq = window.matchMedia('(max-width: 767px)')
		const update = () => setIsMobile(mq.matches)
		mq.addEventListener('change', update)
		return () => mq.removeEventListener('change', update)
	}, [])

	const selector =
		step && isMobile && step.selectorMobile
			? step.selectorMobile
			: step?.selector

	const [rect, setRect] = useState<ReturnType<typeof getRect> | null>(null)

	useLayoutEffect(() => {
		if (!open || !selector) return
		setRect(getRect(document.querySelector(selector)))
	}, [open, selector, stepIndex])

	useEffect(() => {
		if (!open) return

		const update = () => {
			setRect(getRect(document.querySelector(selector)))
		}
		update()
		window.addEventListener('resize', update)
		window.addEventListener('scroll', update, true)
		return () => {
			window.removeEventListener('resize', update)
			window.removeEventListener('scroll', update, true)
		}
	}, [open, selector])

	useEffect(() => {
		if (!open || !selector) return

		let cancelled = false
		const tryLocate = (attempt = 0) => {
			if (cancelled) return
			const el = document.querySelector(selector)
			if (el) {
				setRect(getRect(el))
				if ('scrollIntoView' in el) {
					;(el as any).scrollIntoView({
						behavior: 'smooth',
						block: 'center',
						inline: 'center',
					})
				}
				return
			}
			if (attempt < 30) {
				setTimeout(() => tryLocate(attempt + 1), 50)
			}
		}

		tryLocate()
		return () => {
			cancelled = true
		}
	}, [open, selector, stepIndex])

	if (!open || steps.length === 0 || !step) return null

	const total = steps.length
	const nLabel = `${stepIndex + 1}/${total}`

	const close = () => {
		try {
			localStorage.setItem(storageKey, '1')
		} catch {
			// ignore
		}
		onOpenChange(false)
	}

	const next = () => {
		if (stepIndex >= total - 1) {
			close()
			return
		}
		setStepIndex((i) => Math.min(i + 1, total - 1))
	}

	const prev = () => setStepIndex((i) => Math.max(i - 1, 0))

	// Avoid initial misplaced render before we have a target rect
	if (!rect) {
		return createPortal(
			<div className="fixed inset-0 z-[99999]">
				<div className="absolute inset-0 bg-black/40" onClick={next} />
			</div>,
			document.body
		)
	}

	// Positioning: below target when possible, otherwise above.
	const preferredTop = rect ? rect.bottom + 12 : 100
	const popoverWidth = 260
	const left = rect
		? Math.min(Math.max(rect.left, 12), window.innerWidth - popoverWidth - 12)
		: 12

	const placeAbove = rect
		? preferredTop + 220 > window.innerHeight && rect.top - 12 > 220
		: false
	const top = rect
		? placeAbove
			? Math.max(12, rect.top - 12 - 220)
			: preferredTop
		: 100

	const arrowX = rect
		? Math.min(
				Math.max(rect.left + rect.width / 2 - left, 18),
				popoverWidth - 18
			)
		: popoverWidth / 2

	const highlightStyle: React.CSSProperties | undefined = rect
		? {
				position: 'fixed',
				top: rect.top - 6,
				left: rect.left - 6,
				width: rect.width + 12,
				height: rect.height + 12,
				borderRadius: 12,
				boxShadow: '0 0 0 3px var(--guided-tour-highlight-color)',
				border: '1px solid var(--guided-tour-highlight-color)',
				pointerEvents: 'none',
				zIndex: 100000,
			}
		: undefined

	const popover = (
		<div className="fixed inset-0 z-[99999] [--guided-tour-highlight-color:var(--primary)] dark:[--guided-tour-highlight-color:var(--main-accent)]">
			{/* Dim overlay */}
			<div className="absolute inset-0 bg-black/55" onClick={next} />

			{/* Highlight */}
			{rect && <div style={highlightStyle} />}

			{/* Popover */}
			<div
				className={cn(
					'fixed rounded-xl border border-primary-foreground/20 bg-accent shadow-xl p-4 w-[260px] text-accent'
				)}
				style={{ top, left, zIndex: 100001 }}
				role="dialog"
				aria-modal="true"
			>
				{/* Pointer */}
				<div
					className={cn(
						'absolute w-3 h-3 bg-accent rotate-45',
						placeAbove
							? 'bottom-[-6px] border-r border-b'
							: 'top-[-6px] border-l border-t'
					)}
					style={{ left: arrowX - 6 }}
				/>

				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<p className="text-xs text-primary">{nLabel}</p>
						<p className="text-sm font-semibold mt-1 text-primary">
							{step.title}
						</p>
					</div>
					<Button
						variant="ghost"
						size="icon"
						className="h-8 w-8 absolute top-2 right-2"
						onClick={close}
					>
						<X className="h-4 w-4 text-primary" />
					</Button>
				</div>

				<p className="text-sm text-primary mt-2">{step.description}</p>

				<div
					className={cn(
						'flex items-center mt-4',
						stepIndex === 0 && 'justify-end',
						stepIndex === total - 1 && 'justify-start',
						stepIndex > 0 && stepIndex < total - 1 && 'justify-between'
					)}
				>
					{stepIndex > 0 && (
						<Button
							variant="outline"
							onClick={prev}
							className="rounded-full px-4"
						>
							<ChevronLeft className="h-4 w-4 mr-1" />
						</Button>
					)}

					{stepIndex < total - 1 && (
						<Button
							variant="outline"
							onClick={next}
							className="rounded-full px-4"
						>
							<ChevronRight className="h-4 w-4" />
						</Button>
					)}
				</div>
			</div>
		</div>
	)

	return createPortal(popover, document.body)
}
